import type { NormalizedWidgetFeatures } from "../../features/customization/lib/config-schema";

// All available tabs in priority order
const ALL_TABS = ["Portfolio", "Rewards", "DeFi", "Swap", "Discover"] as const;

export type TabName = (typeof ALL_TABS)[number];

// Icon mapping for each tab
export const TAB_ICONS: Record<TabName, string> = {
  Portfolio: "trending_up",
  Rewards: "featured_seasonal_and_gifts",
  DeFi: "account_balance",
  Swap: "swap_vert",
  Discover: "explore",
} as const;

// Route to tab name mapping
export const ROUTE_TO_TAB: Record<string, TabName> = {
  "/": "Portfolio",
  "/rewards": "Rewards",
  "/defi": "DeFi",
  "/swap": "Swap",
  "/discover": "Discover",
} as const;

// Tab name to route mapping
export const TAB_TO_ROUTE: Record<TabName, string> = {
  Portfolio: "/portfolio/assets",
  Rewards: "/rewards",
  DeFi: "/defi/portfolio/positions",
  Swap: "/swap",
  Discover: "/discover",
} as const;

/**
 * Get the first enabled feature/tab
 */
export function getDefaultTab(features: NormalizedWidgetFeatures): TabName {
  return (
    ALL_TABS.find((tab) => {
      const featureKey = tab.toLowerCase() as keyof NormalizedWidgetFeatures;
      return features[featureKey] === true;
    }) || "Portfolio"
  );
}

/**
 * Get enabled tabs based on features
 */
export function getEnabledTabs(features: NormalizedWidgetFeatures): TabName[] {
  return ALL_TABS.filter((tab) => {
    const featureKey = tab.toLowerCase() as keyof NormalizedWidgetFeatures;
    return features[featureKey] === true;
  });
}

/**
 * Get tab to route mapping for only enabled features
 */
export function getEnabledTabRoutes(
  features: NormalizedWidgetFeatures,
): Partial<Record<TabName, string>> {
  return {
    ...(features.portfolio && { Portfolio: TAB_TO_ROUTE.Portfolio }),
    ...(features.rewards && { Rewards: TAB_TO_ROUTE.Rewards }),
    ...(features.defi && { DeFi: TAB_TO_ROUTE.DeFi }),
    ...(features.swap && { Swap: TAB_TO_ROUTE.Swap }),
    ...(features.discover && { Discover: TAB_TO_ROUTE.Discover }),
  };
}

export function createConfigKey(tabData: string[]): string {
  return tabData.join("-");
}

export function getValidActiveTab(activeTab: string, tabData: string[]): string {
  return tabData.includes(activeTab) ? activeTab : tabData[0];
}
